import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/utils/api";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { compressImage, validateImageFile } from "@/utils/imageUtils";
import { Upload, X, Image as ImageIcon } from "lucide-react";

const PostAd = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);
  const [citySearch, setCitySearch] = useState("");
  const [images, setImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [calculatedCost, setCalculatedCost] = useState(0);
  const [postingStatus, setPostingStatus] = useState({
    wallet_balance: 0,
    free_ads_limit: 2,
    free_ads_used: 0,
    free_ads_remaining: 2,
    can_post_free: true,
    can_post_paid: false,
    can_select_multiple_cities: false,
    posting_mode: "free"
  });
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category_id: "",
    contact_whatsapp: "",
    contact_phone: "",
    contact_telegram: "",
    age: "",
    auto_bump_enabled: false,
    auto_bump_interval: 4,
  });
  const [loading, setLoading] = useState(false);

  // Character limits
  const TITLE_MAX_LENGTH = 45;
  const DESC_MAX_LENGTH = 1000;

  const timeSlotOptions = [
    { value: "6am-12pm", label: "6 AM - 12 PM (Morning)" },
    { value: "12pm-6pm", label: "12 PM - 6 PM (Afternoon)" },
    { value: "6pm-12am", label: "6 PM - 12 AM (Evening)" },
    { value: "12am-6am", label: "12 AM - 6 AM (Night)" },
  ];

  useEffect(() => {
    calculateCost();
  }, [selectedTimeSlots, selectedCities]);

  const calculateCost = () => {
    const numSlots = selectedTimeSlots.length;
    const numCities = selectedCities.length;
    
    if (numSlots === 0 || numCities === 0) {
      setCalculatedCost(0);
      return;
    }

    let pricePerCity = 0;
    if (numSlots === 1) {
      pricePerCity = 400;
    } else if (numSlots === 2) {
      pricePerCity = 700;
    } else {
      pricePerCity = 1200; // 3 or 4 slots = 24 hours
    }

    setCalculatedCost(pricePerCity * numCities);
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [categoriesRes, citiesRes, postingStatusRes] = await Promise.all([
        api.get("/categories"),
        api.get("/cities"),
        api.get("/user/posting-status")
      ]);
      setCategories(categoriesRes.data);
      setCities(citiesRes.data);
      setPostingStatus(postingStatusRes.data);
      
      // If user can't post at all, show message and redirect
      if (postingStatusRes.data.posting_mode === "none") {
        toast.error("You have used all free ads. Please add money to your wallet to post more.");
        navigate('/wallet');
      }
    } catch (error) {
      toast.error("Failed to load data");
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleCityToggle = (cityId) => {
    // If user can't select multiple cities (no wallet balance), only allow one city
    if (!postingStatus.can_select_multiple_cities) {
      setSelectedCities([cityId]);
    } else {
      if (selectedCities.includes(cityId)) {
        setSelectedCities(selectedCities.filter(id => id !== cityId));
      } else {
        setSelectedCities([...selectedCities, cityId]);
      }
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    if (images.length + files.length > 3) {
      toast.error("You can upload maximum 3 photos");
      return;
    }

    setUploadingImages(true);
    const newImages = [];

    for (const file of files) {
      const validation = validateImageFile(file);
      if (!validation.valid) {
        toast.error(validation.error);
        continue;
      }

      try {
        const compressedImage = await compressImage(file, 800, 800, 0.7);
        newImages.push(compressedImage);
      } catch (error) {
        console.error("Error compressing image:", error);
        toast.error(`Failed to process ${file.name}`);
      }
    }

    setImages([...images, ...newImages].slice(0, 3));
    setUploadingImages(false);
    toast.success(`${newImages.length} photo(s) uploaded and compressed`);
  };

  const handleRemoveImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleTimeSlotToggle = (slot) => {
    // For free ads (posting_mode === "free"), only allow ONE time slot
    if (postingStatus.posting_mode === "free") {
      // If clicking the same slot, deselect it
      if (selectedTimeSlots.includes(slot)) {
        setSelectedTimeSlots([]);
      } else {
        // Select only this slot (radio behavior)
        setSelectedTimeSlots([slot]);
      }
    } else {
      // For paid ads, allow multiple selections
      if (selectedTimeSlots.includes(slot)) {
        setSelectedTimeSlots(selectedTimeSlots.filter(s => s !== slot));
      } else {
        setSelectedTimeSlots([...selectedTimeSlots, slot]);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Enforce character limits (UX + safety)
    if ((formData.title || "").length > TITLE_MAX_LENGTH) {
      toast.error(`Title must be at most ${TITLE_MAX_LENGTH} characters`);
      return;
    }
    if ((formData.description || "").length > DESC_MAX_LENGTH) {
      toast.error(`Description must be at most ${DESC_MAX_LENGTH} characters`);
      return;
    }

    if (selectedCities.length === 0) {
      toast.error("Please select at least one city");
      return;
    }

    if (selectedTimeSlots.length === 0) {
      toast.error("Please select at least one time slot");
      return;
    }

    // For paid ads, check wallet balance
    if (postingStatus.posting_mode === "paid" && postingStatus.wallet_balance < calculatedCost) {
      toast.error(`Insufficient wallet balance. Required: ₹${calculatedCost}, Available: ₹${postingStatus.wallet_balance}`);
      navigate('/wallet');
      return;
    }

    setLoading(true);
    try {
      const adData = {
        ...formData,
        city_ids: selectedCities,
        time_slots: selectedTimeSlots,
        images: images,
      };
      // Add age only if provided
      if (formData.age) {
        adData.age = parseInt(formData.age);
      }
      const response = await api.post("/ads", adData);
      toast.success(`Ad posted successfully! Cost: ₹${response.data.cost}. Your ad is now live.`);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to post ad");
    } finally {
      setLoading(false);
    }
  };

  const filteredCities = cities.filter((city) => {
    const q = citySearch.toLowerCase().trim();
    if (!q) return true;
    return (
      (city.name || "").toLowerCase().includes(q) ||
      (city.slug || "").toLowerCase().includes(q) ||
      (city.state || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <Header />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2" style={{fontFamily: 'Inter, sans-serif'}}>
            Post New Ad
          </h1>
          <p className="text-gray-400">Fill in the details below to create your ad</p>
          
          {/* Posting Status Banner */}
          {postingStatus.posting_mode === "free" && postingStatus.free_ads_remaining > 0 && (
            <div className="mt-4 p-4 bg-green-600/20 border border-green-500/50 rounded-lg">
              <p className="text-green-400 font-bold text-lg flex items-center gap-2">
                🎉 You have {postingStatus.free_ads_remaining} FREE ad{postingStatus.free_ads_remaining > 1 ? 's' : ''} remaining!
              </p>
              <p className="text-gray-300 text-sm mt-1">
                Free ads are limited to 1 city. Add money to your wallet for multiple cities and premium placement.
              </p>
            </div>
          )}
            
            {postingStatus.posting_mode === "paid" && (
              <div className="mt-4 p-4 bg-purple-600/20 border border-purple-500/50 rounded-lg">
                <p className="text-purple-400 font-bold text-lg flex items-center gap-2">
                  💳 Premium Mode - Wallet Balance: ₹{postingStatus.wallet_balance}
                </p>
                <p className="text-gray-300 text-sm mt-1">
                  You can select multiple cities for maximum reach. Your ads will be shown as premium listings.
                </p>
              </div>
            )}
          
          {/* Pricing Info */}
          <div className="mt-4 p-4 bg-blue-600/20 border border-blue-500/50 rounded-lg">
            <p className="text-white font-semibold mb-2">Pricing (Valid for 3 days):</p>
            <ul className="text-sm text-gray-300 space-y-1">
              <li>• 1 Time Slot: ₹400 per city (3 days)</li>
              <li>• 2 Time Slots: ₹700 per city (3 days)</li>
              <li>• 3-4 Time Slots (24 hours): ₹1,200 per city (3 days)</li>
            </ul>
            {calculatedCost > 0 && (
              <div className="mt-3 pt-3 border-t border-blue-500/30">
                {postingStatus.posting_mode === "free" ? (
                  <>
                    <p className="text-green-400 font-bold text-lg">
                      🆓 This ad is FREE!
                    </p>
                    <p className="text-gray-400 text-sm">
                      (Regular price: ₹{calculatedCost})
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-yellow-400 font-bold text-lg">
                      Total Cost: ₹{calculatedCost}
                    </p>
                    <p className="text-gray-400 text-sm">
                      ({selectedTimeSlots.length} slot{selectedTimeSlots.length > 1 ? 's' : ''} × {selectedCities.length} city/cities)
                    </p>
                    <p className="text-gray-400 text-sm">
                      Wallet Balance: ₹{postingStatus.wallet_balance}
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <Card className="p-8 bg-white/5 backdrop-blur-sm border-white/10">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Title * <span className="text-gray-500">({formData.title.length}/{TITLE_MAX_LENGTH} chars max)</span>
              </label>
              <Input
                data-testid="post-ad-title-input"
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                maxLength={TITLE_MAX_LENGTH}
                placeholder="Enter a catchy title (max 45 characters)"
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
              />
              {formData.title.length > TITLE_MAX_LENGTH * 0.9 && (
                <p className="text-yellow-400 text-xs mt-1">Approaching character limit</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description * <span className="text-gray-500">({formData.description.length}/{DESC_MAX_LENGTH} chars max)</span>
              </label>
              <Textarea
                data-testid="post-ad-description-input"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows={8}
                maxLength={DESC_MAX_LENGTH}
                placeholder="Describe your service in detail (up to 1000 characters)"
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
              />
              <p className="text-gray-500 text-xs mt-1">
                Characters: {formData.description.length}/{DESC_MAX_LENGTH}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Age (optional)</label>
              <Input
                data-testid="post-ad-age-input"
                type="number"
                name="age"
                value={formData.age}
                onChange={handleChange}
                min="18"
                max="99"
                placeholder="Enter age (18+)"
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 w-32"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Add Photos (Max 3, 9:16 Portrait Recommended) *
              </label>
              <p className="text-gray-500 text-xs mb-3">For best results, upload vertical photos (9:16 aspect ratio)</p>
              <div className="space-y-4">
                {/* Image Preview */}
                {images.length > 0 && (
                  <div className="grid grid-cols-3 gap-4">
                    {images.map((img, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={img}
                          alt={`Upload ${index + 1}`}
                          className="w-full h-48 object-cover rounded-lg border border-white/10"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(index)}
                          className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Button */}
                {images.length < 3 && (
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      multiple
                      onChange={handleImageUpload}
                      disabled={uploadingImages}
                      className="hidden"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      className="flex items-center justify-center gap-2 w-full h-32 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-white/5 transition-all"
                    >
                      {uploadingImages ? (
                        <span className="text-gray-400">Compressing images...</span>
                      ) : (
                        <>
                          <Upload className="w-6 h-6 text-gray-400" />
                          <span className="text-gray-400">
                            Click to upload {images.length > 0 ? `(${3 - images.length} more)` : '(up to 3 photos)'}
                          </span>
                        </>
                      )}
                    </label>
                  </div>
                )}

                <p className="text-sm text-gray-400">
                  <ImageIcon className="w-4 h-4 inline mr-1" />
                  Photos will be automatically compressed. Supported formats: JPG, PNG, WebP
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Category *</label>
              <Select
                value={formData.category_id}
                onValueChange={(value) => setFormData({ ...formData, category_id: value })}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="post-ad-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Select City {!postingStatus.can_select_multiple_cities ? '(1 city for free ads) *' : '(Multiple allowed) *'}
              </label>
              {!postingStatus.can_select_multiple_cities && (
                <p className="text-yellow-400 text-xs mb-3">
                  ⚠️ Free ads can only be posted in ONE city. Add money to wallet to select multiple cities.
                </p>
              )}
              <Input
                type="text"
                placeholder="Search cities..."
                value={citySearch}
                onChange={(e) => setCitySearch(e.target.value)}
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 mb-3"
              />
              <div className="max-h-60 overflow-y-auto bg-white/5 border border-white/10 rounded-lg p-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {filteredCities.map((city) => (
                    <div key={city.id} className="flex items-center space-x-2">
                      {!postingStatus.can_select_multiple_cities ? (
                        // Radio button for free ads (single selection)
                        <input
                          type="radio"
                          id={`city-${city.id}`}
                          name="city-selection"
                          checked={selectedCities.includes(city.id)}
                          onChange={() => handleCityToggle(city.id)}
                          className="w-4 h-4 text-blue-600 bg-white/5 border-white/20 focus:ring-blue-500"
                        />
                      ) : (
                        // Checkbox for paid ads (multiple selection)
                        <Checkbox
                          id={`city-${city.id}`}
                          checked={selectedCities.includes(city.id)}
                          onCheckedChange={() => handleCityToggle(city.id)}
                        />
                      )}
                      <label htmlFor={`city-${city.id}`} className="text-sm text-white cursor-pointer">
                        {city.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-sm text-gray-400 mt-2">
                Selected: {selectedCities.length} {selectedCities.length === 1 ? 'city' : 'cities'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Select Time Slot{postingStatus.posting_mode !== "free" ? 's' : ''} *
              </label>
              
              {/* Free ads can only select ONE time slot */}
              {postingStatus.posting_mode === "free" && (
                <p className="text-yellow-400 text-xs mb-3">
                  ⚠️ Free ads can only select ONE time slot. Add money to wallet for 24-hour visibility.
                </p>
              )}
              
              {/* 24 Hours Option - Only for PAID ads */}
              {postingStatus.posting_mode === "paid" && (
                <div className="mb-4 p-4 bg-purple-600/20 border border-purple-500/50 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="slot-24hours"
                      checked={selectedTimeSlots.length === 4}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedTimeSlots(["6am-12pm", "12pm-6pm", "6pm-12am", "12am-6am"]);
                        } else {
                          setSelectedTimeSlots([]);
                        }
                      }}
                    />
                    <label htmlFor="slot-24hours" className="text-white font-semibold cursor-pointer flex items-center">
                      <span className="text-lg">⭐ 24 Hours (All Day) - Recommended</span>
                    </label>
                  </div>
                  <p className="text-gray-300 text-sm mt-2 ml-6">
                    Best value! Your ad will be visible all day long - ₹1,200 per city
                  </p>
                </div>
              )}

              {/* Individual Time Slots */}
              {postingStatus.posting_mode === "paid" && (
                <p className="text-gray-400 text-sm mb-3">Or select individual time slots:</p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {timeSlotOptions.map((slot) => (
                  <div key={slot.value} className="flex items-center space-x-2 p-3 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors">
                    {postingStatus.posting_mode === "free" ? (
                      // Radio buttons for free ads (single selection)
                      <input
                        type="radio"
                        id={`slot-${slot.value}`}
                        name="time-slot-selection"
                        checked={selectedTimeSlots.includes(slot.value)}
                        onChange={() => handleTimeSlotToggle(slot.value)}
                        className="w-4 h-4 text-pink-600 bg-white/5 border-white/20 focus:ring-pink-500"
                      />
                    ) : (
                      // Checkboxes for paid ads (multiple selection)
                      <Checkbox
                        id={`slot-${slot.value}`}
                        checked={selectedTimeSlots.includes(slot.value)}
                        onCheckedChange={() => handleTimeSlotToggle(slot.value)}
                      />
                    )}
                    <label htmlFor={`slot-${slot.value}`} className="text-white cursor-pointer flex-1">
                      {slot.label}
                    </label>
                  </div>
                ))}
              </div>
              <p className="text-gray-400 text-sm mt-3">
                Selected: {selectedTimeSlots.length} slot{selectedTimeSlots.length !== 1 ? 's' : ''} 
                {postingStatus.posting_mode === "free" ? (
                  <span className="text-green-400 font-semibold ml-2">🆓 FREE</span>
                ) : (
                  <>
                    {selectedTimeSlots.length === 1 && ' (₹400/city)'}
                    {selectedTimeSlots.length === 2 && ' (₹700/city)'}
                    {selectedTimeSlots.length >= 3 && ' (₹1,200/city)'}
                  </>
                )}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">WhatsApp Number *</label>
                <Input
                  data-testid="post-ad-whatsapp-input"
                  type="tel"
                  name="contact_whatsapp"
                  value={formData.contact_whatsapp}
                  onChange={handleChange}
                  required
                  placeholder="919876543210"
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                />
                <p className="text-xs text-gray-500 mt-1">Add country code (e.g., 91 for India)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Phone Number *</label>
                <Input
                  data-testid="post-ad-phone-input"
                  type="tel"
                  name="contact_phone"
                  value={formData.contact_phone}
                  onChange={handleChange}
                  required
                  placeholder="919876543210"
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                />
                <p className="text-xs text-gray-500 mt-1">Add country code (e.g., 91 for India)</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Telegram (Optional)</label>
              <Input
                type="text"
                name="contact_telegram"
                value={formData.contact_telegram}
                onChange={handleChange}
                placeholder="username or 919876543210"
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
              />
              <p className="text-xs text-gray-500 mt-1">Enter username (without @) or mobile with country code</p>
            </div>

            <div className="flex items-center space-x-2 p-4 bg-white/5 rounded-lg border border-white/10">
              <Checkbox
                id="auto-bump"
                name="auto_bump_enabled"
                checked={formData.auto_bump_enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, auto_bump_enabled: checked })}
              />
              <label htmlFor="auto-bump" className="text-sm text-white cursor-pointer">
                Enable Auto-Bump (Automatically bump your ad every 4 hours)
              </label>
            </div>

            <div className="flex gap-4">
              <Button
                data-testid="post-ad-submit-button"
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                {loading ? "Posting..." : "Post Ad"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      </div>

      <Footer />
    </div>
  );
};

export default PostAd;
